import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const TOUR = [
  { src: '/screenshots/tour-youth.png', label: 'Learner Home' },
  { src: '/screenshots/tour-casemanager.png', label: 'Case Manager' },
  { src: '/screenshots/tour-admin.png', label: 'Admin Analytics' },
  { src: '/screenshots/tour-learner.png', label: 'Intake & Pathways' },
];

export const Nav = ({ onSchedule }) => {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState('');

  React.useEffect(() => {
    const ids = ['crisis', 'platform', 'model', 'outcomes', 'faq', 'ask'];
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: '-30% 0px -65% 0px' }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const lnk = (href, label, testid) => (
    <li key={href}>
      <a href={href} data-testid={testid} className={active === href.slice(1) ? 'nav-active' : ''}>
        {label}
      </a>
    </li>
  );

  return (
    <>
      <nav className="mkt-nav" data-testid="mkt-nav" aria-label="Main navigation">
        <div className="n-brand"><img src="/logo.png" alt="AssistivOS" style={{ height: 30, width: 'auto', display: 'block' }} /></div>
        <ul className="n-links">
          {lnk('#crisis',   'The Problem', 'nav-link-problem')}
          {lnk('#platform', 'Platform',    'nav-link-platform')}
          {lnk('#model',    'Model',       'nav-link-model')}
          {lnk('#outcomes', 'Outcomes',    'nav-link-outcomes')}
          {lnk('#faq',      'FAQ',         'nav-link-faq')}
          {lnk('#ask',      'Invest',      'nav-link-invest')}
        </ul>
        <div className="n-right">
          <Link to="/login" className="n-signin" data-testid="nav-signin">Sign In</Link>
          <button type="button" className="n-cta" data-track="nav-schedule" data-testid="nav-cta-call" onClick={onSchedule}>Schedule a Call</button>
          <button type="button" className="n-burger" data-testid="nav-mobile-toggle" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>☰</button>
        </div>
      </nav>
      {open && (
        <div className="n-mobile" data-testid="nav-mobile-menu" onClick={() => setOpen(false)}>
          {[['#crisis','The Problem'],['#platform','Platform'],['#model','Model'],['#outcomes','Outcomes'],['#faq','FAQ'],['#ask','Invest']].map(([href, label]) => (
            <a key={href} href={href} className={active === href.slice(1) ? 'nav-active' : ''}>{label}</a>
          ))}
          <Link to="/login">Sign In</Link>
        </div>
      )}
    </>
  );
};

const HERO_CLIPS = [
  { key: 'documentary', label: 'Workshop Documentary', mp4: 'https://customer-assets-rejwkqb3.emergentagent.net/job_justice-forward-1/artifacts/38o3xvkk_ChatGPT-workshop-documentary.mp4' },
  { key: 'portrait',    label: 'Animated Portrait',    mp4: 'https://customer-assets-rejwkqb3.emergentagent.net/job_justice-forward-1/artifacts/2zbrs3al_ChatGPT_animated-portrait-7s.mp4' },
  { key: 'welding',     label: 'Skilled Trades',       webm: '/assets/hero-welding.webm', mp4: '/assets/hero-welding.mp4',    poster: '/assets/hero-poster.jpg' },
  { key: 'repair',      label: 'Computer Repair',      mp4: 'https://customer-assets-rejwkqb3.emergentagent.net/job_justice-forward-1/artifacts/anjx7q1j_ChatGPT-computer_repair-documentary.mp4' },
  { key: 'circuit',     label: 'Tech Careers',         webm: '/assets/hero-circuit.webm', mp4: '/assets/hero-circuit.mp4',   poster: '/assets/hero-circuit-poster.jpg' },
];

let heroClipPicked = null;
const pickHeroClipIdx = () => {
  if (heroClipPicked !== null) return heroClipPicked;
  let idx = 0;
  try {
    idx = (parseInt(localStorage.getItem('aos_hero_clip') || '-1', 10) + 1) % HERO_CLIPS.length;
    if (Number.isNaN(idx) || idx < 0) idx = 0;
    localStorage.setItem('aos_hero_clip', String(idx));
  } catch (_e) { idx = 0; }
  heroClipPicked = idx;
  return heroClipPicked;
};

const PARTICLES = 14;

export const Hero = ({ onBrief }) => {
  const [slide, setSlide] = useState(0);
  const [clipIdx, setClipIdx] = useState(pickHeroClipIdx);

  // Event-driven rotation: advance the moment a clip's `ended` event fires.
  // A 14-second ceiling timer acts as a hard fallback for stalled or very long clips.
  useEffect(() => {
    const advance = () => setClipIdx((v) => (v + 1) % HERO_CLIPS.length);

    // Find the currently active <video> element by its data-testid
    const video = document.querySelector(
      `[data-testid="hero-video-${HERO_CLIPS[clipIdx].key}"]`
    );

    // Ceiling timer — always set; clears itself when ended fires first
    const ceiling = setTimeout(() => {
      if (video) video.removeEventListener('ended', onEnded);
      advance();
    }, 14000);

    function onEnded() {
      clearTimeout(ceiling);
      advance();
    }

    if (video) video.addEventListener('ended', onEnded, { once: true });

    return () => {
      clearTimeout(ceiling);
      if (video) video.removeEventListener('ended', onEnded);
    };
  }, [clipIdx]);

  const clip = HERO_CLIPS[clipIdx];
  useEffect(() => {
    const t = setInterval(() => setSlide((v) => (v + 1) % TOUR.length), 3400);
    return () => clearInterval(t);
  }, []);
  const particles = useMemo(() => Array.from({ length: PARTICLES }, (_, i) => ({
    id: i,
    left: `${5 + Math.random() * 90}%`,
    top: `${8 + Math.random() * 84}%`,
    size: 2 + Math.random() * 2,
    fx: `${(Math.random() * 24 - 12).toFixed(0)}px`,
    fy: `${-(20 + Math.random() * 20).toFixed(0)}px`,
    dur: `${(6 + Math.random() * 6).toFixed(1)}s`,
    del: `${(Math.random() * 5).toFixed(1)}s`,
  })), []);

  return (
    <div className="hero" data-label="Overview" data-testid="hero-section">
      <div className="hero-bg" aria-hidden="true" />
      <div className="hero-aurora" aria-hidden="true" />
      <div className="hero-video-stack" aria-hidden="true">
        {HERO_CLIPS.map((c, i) => (
          <video
            key={c.key}
            className={`hero-video${i === clipIdx ? ' on' : ''}`}
            data-testid={`hero-video-${c.key}`}
            autoPlay
            muted
            playsInline
            preload="auto"
            poster={c.poster || undefined}
            tabIndex={-1}
            style={c.key === 'portrait' ? { transform: 'scaleX(-1)' } : undefined}
            ref={(el) => { if (el) { el.muted = true; el.play().catch(() => {}); } }}
          >
            {c.webm && <source src={c.webm} type="video/webm" />}
            <source src={c.mp4} type="video/mp4" />
          </video>
        ))}
      </div>
      <div className="hero-ov1" aria-hidden="true" />
      <div className="mesh1" aria-hidden="true" /><div className="mesh2" aria-hidden="true" /><div className="mesh3" aria-hidden="true" />
      <div className="hero-grid-dots" aria-hidden="true" />
      <div className="sweeps" aria-hidden="true"><div className="sw" /><div className="sw" /><div className="sw" /></div>
      <div className="hero-ov2" aria-hidden="true" />
      <div className="hero-noise" aria-hidden="true" />
      {particles.map((p) => (
        <span key={p.id} className="hp" aria-hidden="true" style={{ left: p.left, top: p.top, width: p.size, height: p.size, '--fx': p.fx, '--fy': p.fy, '--dur': p.dur, '--del': p.del }} />
      ))}

      <div className="hero-left">
        <div className="hero-tag">
          <div className="hero-tag-dot" />
          <span className="hero-tag-text">HDG University · Tech Tradesmith Institute · Plano, Texas</span>
        </div>
        <h1 className="hero-h">
          <span className="dim">600,000 People</span><br />
          Leave Prison Each Year.<br />
          Most Leave With <span className="grad-text">Nothing.</span>
        </h1>
        <p className="hero-sub">
          For two decades, billions were spent on correctional programming
          with no infrastructure to prove it worked, connect it to careers, or
          sustain it beyond the gate. AssistivOS — built by HDG University and Tech
          Tradesmith Institute — is the operating system built to change that. Finally.
        </p>
        <div className="hero-btns">
          <Link to="/prototype" className="btn-or magnetic" data-track="hero-platform" data-testid="hero-cta-platform">View Live Platform →</Link>
          <button type="button" className="btn-wh magnetic" data-track="hero-brief" data-testid="hero-cta-brief" onClick={onBrief}>Download Investment Brief ↓</button>
        </div>
        <div className="hero-proof">
          <div className="proof-pip">Prototype Live Now</div>
          <div className="proof-pip">Workforce Pell Compliant</div>
          <div className="proof-pip">Texas-Based · National Vision</div>
        </div>
        <div className="hero-scene" aria-hidden="true" data-testid="hero-scene-caption">
          <span className="hero-scene-dot" />
          <span key={clip.key} className="hero-scene-label">Now showing · {clip.label}</span>
          <span className="hero-scene-sub">Workshop · Portrait · Skilled Trades · Tech Careers</span>
        </div>
      </div>

      <div className="hero-right">
        <div className="hero-chips-layer" style={{ position: 'relative' }}>
          <div className="tablet-outer" data-testid="hero-tablet">
            <div className="t-spk" />
            <div className="t-scr t-tour" data-testid="hero-tablet-tour">
              {TOUR.map((s, idx) => (
                <img key={s.src} src={s.src} alt={s.label} draggable={false} loading={idx === 0 ? 'eager' : 'lazy'} className={`tour-slide${idx === slide ? ' on' : ''}`} />
              ))}
              <div className="tour-chrome">
                <div className="tour-label">▶ Live Prototype · {TOUR[slide].label}</div>
                <div className="tour-dots" data-testid="hero-tour-dots">
                  {TOUR.map((_, d) => <span key={d} className={d === slide ? 'on' : ''} />)}
                </div>
              </div>
            </div>
            <div className="t-hb" />
          </div>
          <div className="chip c1"><div className="ch-l">Credential Progress</div><div className="ch-v"><em>68%</em> EPA 608</div></div>
          <div className="chip c2"><div className="ch-l">Days to Release</div><div className="ch-v"><em>47</em> days left</div></div>
          <div className="chip c3"><div className="ch-l">Employer Matches</div><div className="ch-v"><em>4</em> active</div></div>
          <div className="chip c4"><div className="ch-l">XP This Week</div><div className="ch-v"><em>+340</em> XP</div></div>
        </div>
        <div className="hero-stats" style={{ marginTop: 24 }} data-testid="hero-stats">
          <div className="hs-cell"><span className="hs-n">1.97M</span><div className="hs-l">Americans Incarcerated Today (2026)</div></div>
          <div className="hs-cell"><span className="hs-n">68%</span><div className="hs-l">Re-arrested within 3 years</div></div>
          <div className="hs-cell"><span className="hs-n">43%</span><div className="hs-l">Lower Recidivism with Education or Training</div></div>
        </div>
      </div>
    </div>
  );
};

const MQ_ITEMS = [
  '1.97M Americans Incarcerated', '68% Re-arrested Within 3 Years', '43% Lower Recidivism With Education or Training (RAND)',
  '$5 Returned Per $1 Invested', '$40.8B+ in Federal Pell Grant Capacity', '$180B Annual Cost of Incarceration',
  'The First Regionalized Correctional Education Marketplace',
];

export const Marquee = () => (
  <div className="marquee-wrap" aria-hidden="true" data-testid="marquee">
    <div className="marquee-track">
      {[0, 1].map((dup) => MQ_ITEMS.map((t, i) => (
        <div className="mq-item" key={`${dup}-${i}`}>{t}<span className="mq-sep">·</span></div>
      )))}
    </div>
  </div>
);

const CRISIS_CELLS = [
  { n: '1.97M', t: 'Americans Incarcerated Today', d: 'More than any nation on earth. Each costs taxpayers $35,000+ per year in direct incarceration.' },
  { n: '68%', t: 'Re-arrested Within 3 Years', d: 'A cycle that compounds across generations, communities, and the $180B annual corrections budget.' },
  { n: '43%', t: 'Lower Reincarceration With Education or Training', d: "RAND's most rigorous finding. The intervention works. The infrastructure to deploy it never existed." },
  { n: '$5', t: 'Saved Per $1 Invested', d: 'The highest ROI of any criminal justice investment. Documented. Proven. Undeployed at scale — until now.' },
];

export const Crisis = () => (
  <section className="crisis" id="crisis" data-label="The Problem" data-testid="crisis-section">
    <div className="crisis-wrap">
      <div className="crisis-top">
        <h2 className="crisis-h reveal-l">The System Knows What Works.<br />No One Has Built It — <em>Until Now.</em></h2>
        <p className="crisis-desc reveal-r">The evidence base for correctional education has never been stronger. The infrastructure to deliver it at scale has never existed. Prison problems are big. Big problems call for big solutions. AssistivOS closes that gap — permanently.</p>
      </div>
      <div className="c-grid">
        {CRISIS_CELLS.map((c) => (
          <div className="c-cell" key={c.t}>
            <span className="c-n" data-final={c.n}>{c.n}</span>
            <div className="c-t">{c.t}</div>
            <p className="c-d">{c.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const TIMELINE = [
  { yr: '1994', hot: false, tx: <><strong>Pell Grants eliminated</strong> for incarcerated students. Correctional education loses its primary federal funding. A generation of opportunity disappears overnight.</> },
  { yr: '2003–2015', hot: false, tx: <><strong>Platform fragmentation deepens.</strong> Tablet vendors enter. Content catalogs expand. Integration, credentialing, and employer connectivity remain structurally absent.</> },
  { yr: '2016–2020', hot: false, tx: <><strong>Second Chance Pell Pilot</strong> reaches 67 colleges and 12,000 students. Evidence accumulates. Infrastructure does not follow.</> },
  { yr: '2020', hot: false, tx: <><strong>COVID exposes every gap.</strong> Tablet-only platforms collapse without connectivity. Educational continuity fails simultaneously across thousands of facilities.</> },
  { yr: 'July 2023', hot: true, tx: <><strong>Workforce Pell fully restored.</strong> $40.8B+ in Federal Pell Grant Capacity unlocked. The largest correctional education policy shift in 30 years — with no platform built to absorb it.</> },
  { yr: 'Now', hot: true, tx: <><strong>AssistivOS enters the market</strong> as the first neutral infrastructure layer — vendor-agnostic, Workforce Pell compliant, built to connect every stakeholder in the reentry ecosystem.</> },
];

export const Failure = () => (
  <section className="failure" data-label="The Record" data-testid="failure-section">
    <div className="failure-wrap">
      <div className="reveal-l">
        <div className="eyebrow">The System's Record</div>
        <h2 className="sec-h" style={{ color: 'var(--wh)', marginBottom: 28 }}>Two Decades of Good Intentions. Zero Infrastructure.</h2>
        <p className="f-body">In 1994, Congress eliminated Pell Grants for incarcerated students. For three decades, correctional education became a patchwork — disconnected vendors, siloed case managers, and content catalogs with no pathway to credentials, employers, or evidence that anything worked.</p>
        <p className="f-body">The result was predictable. Not because education doesn't work — RAND proves definitively that it does. But because no platform existed to deliver it at scale, prove it to funders, and sustain it across the moment that defines everything: the day someone walks out.</p>
        <div className="f-pull">
          "The competitive battlefield is shifting from 'who has content?' to 'who controls Pell-compatible delivery, workforce outcomes, and longitudinal evidence?' The market has no neutral infrastructure layer."
          <cite>— Jobs for the Future, 2024 Prison-EdTech Market Scan ($217.8M–$923.7M TAM)</cite>
        </div>
        <p className="f-body">In July 2023, Workforce Pell was restored — a 30-year policy reversal drawing from $40.8B+ in Federal Pell Grant Capacity. The largest correctional education opportunity in a generation arrived. With no platform built to receive it. Until AssistivOS.</p>
      </div>
      <div className="reveal-r">
        <div className="eyebrow">Timeline</div>
        <div className="tl" data-testid="failure-timeline">
          {TIMELINE.map((t) => (
            <div className={`tl-i${t.hot ? ' hot' : ''}`} key={t.yr}>
              <div className="tl-yr">{t.yr}</div>
              <p className="tl-tx">{t.tx}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
